import { UIEvent } from 'react';

import {
  Modal,
  Flex,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalTrigger,
  FloraButton,
} from '@grupoboticario/flora-react';
import { InformationCircleIcon, LockIcon, ArrowLeftIcon } from '@grupoboticario/flora-react-icons';
import slugify from 'slugify';

import { ResponsiveFloraTypography } from 'components';
import config from 'config';
import { FloraProvider } from 'containers/ProviderComposer/providers';
import { isMobile as isMobileHelper } from 'helpers';
import { useTracking } from 'hooks';

import Styles from './ResetPassword.styles';

type ResetPasswordParams = {
  label?: string;
  location?: string;
  eventName?: string;
  pageView?: string;
};

export const ResetPassword = ({
  label = 'Alterar minha senha',
  location = 'perfil/modal-esqueci-minha-senha',
  eventName = 'interaction_modal_alterar_senha',
  pageView = 'view-modal-alterar-senha',
}: ResetPasswordParams) => {
  const isMobile = isMobileHelper();
  const { sendDataToGTM } = useTracking();

  const trackModalOpen = (open: boolean) => {
    if (open) {
      sendDataToGTM({
        event: 'page_view',
        event_parameters: {
          event_name: pageView,
          page_location: `${config.app.url}${location}`,
        },
      });
    }
  };
  const trackTriggerClick = (event: UIEvent) => {
    stopPropagation(event);
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: eventName,
        cd_interaction_detail: `click:${slugify(label, { lower: true })} `,
      },
    });
  };

  const stopPropagation = (event: UIEvent) => {
    event.stopPropagation();
  };

  const trackResetPasswordButton = (event: UIEvent) => {
    stopPropagation(event);
    const target = event.currentTarget as HTMLButtonElement;
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: eventName,
        page_location: `${config.app.url}${location}`,
        cd_interaction_detail: `click:${slugify(target.innerText || 'fechar-modal', {
          lower: true,
          replacement: '-',
        })}`,
      },
    });
  };

  const handleLogout = (event: UIEvent) => {
    stopPropagation(event);
    trackResetPasswordButton(event);

    window.location.href = '/logout?returnUrl=/recuperacao-de-senha';
  };

  return (
    <Modal onOpenChange={trackModalOpen}>
      <Flex>
        <ModalTrigger onClick={trackTriggerClick}>
          <ResponsiveFloraTypography
            textDecoration='underline'
            color='$conversionButtonDefault'
            css={{ cursor: 'pointer' }}
          >
            {label}
          </ResponsiveFloraTypography>
        </ModalTrigger>
      </Flex>
      <Styles.ModalContent size='large'>
        <FloraProvider>
          <ModalHeader>
            <ModalCloseButton onClick={trackResetPasswordButton} />
          </ModalHeader>

          <ModalBody>
            <Styles.Content direction='column' gap='$4'>
              {isMobile ? <InformationCircleIcon size={48} /> : null}

              <ResponsiveFloraTypography fontSize='heading' as='h3' fontWeight='bold'>
                Alterar minha senha
              </ResponsiveFloraTypography>

              <Styles.InfoWrapper direction='column'>
                <Styles.Info>
                  Ao prosseguir, sua conta será deslogada e te levaremos para a área de redefinição
                  de senha.
                </Styles.Info>

                <Styles.Info>Você gostaria de continuar?</Styles.Info>
              </Styles.InfoWrapper>

              <Styles.ButtonWrapper justify='flex-end' gap='$4'>
                <ModalCloseButton asChild>
                  <FloraButton onClick={trackResetPasswordButton} hierarchy='secondary'>
                    <Flex gap='$1'>
                      <ArrowLeftIcon size={16} /> Voltar para o perfil
                    </Flex>
                  </FloraButton>
                </ModalCloseButton>

                <FloraButton onClick={handleLogout}>
                  <Flex gap='$1'>
                    <LockIcon size={16} />
                    Continuar e redefinir minha senha
                  </Flex>
                </FloraButton>
              </Styles.ButtonWrapper>
            </Styles.Content>
          </ModalBody>
        </FloraProvider>
      </Styles.ModalContent>
    </Modal>
  );
};
